<script setup lang="ts">
import type { PdpEcommerceBlockProductDetailsRedesignProps } from '@design-system/components/Pdp/EcommerceBlock/Product/PdpEcommerceBlockProductDetailsRedesign.props'
import type { ProductCardHorizontal } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import type { TemplatesPdpProps } from './TemplatesPdp.props'
import type { ColorOption } from '@design-system/components/Pdp/PdpColorSelector.props'
import {
  PDP_SIMILAR_PRODUCTS_ID,
  PDP_YOU_MAY_ALSO_LIKE_ID_REDESIGN,
} from '@integration-layer/data/xGenIds'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import PdpMessageSideSlide from '@design-system/components/Pdp/EcommerceBlock/PdpMessageSideSlide.vue'
import PdpVirtualMirrorModal from '@design-system/components/Pdp/EcommerceBlock/PdpVirtualMirrorModal.vue'
import type { VirtualMirrorProductData } from '@design-system/components/Pdp/EcommerceBlock/PdpVirtualMirrorModal.props'
import { useShowHeaderSearch } from '@design-system/composables/useShowHeaderSearch'
import { usePdpPaneOwnScroll } from '@design-system/composables/usePdpPaneOwnScroll'

const props = defineProps<TemplatesPdpProps>()
const emit = defineEmits<{
  'update:selectedSizeIndex': [selectedSizeIndex: number | null]
  'update:selectedCountry': [selectedCountry: string]
  'update:selectedColor': [selectedColor: string]
  'update:selectedExtSku': [selectedExtSku: string]
  'update-wishlist': []
  addToCart: []
}>()

const { currentWebsite, isEa7Website, isEmporioArmaniWebsite } = useWebsite()
const { openSearchView } = useMenu()
const { openDialog } = useDialog()
const { addProductToRecentlyViewedCookie: addProductToRecentlyViewed } =
  useRecentlyViewed('recently-viewed')

const { fetchAndMapProductData } = useTemplatePdp()
const { addToCartPayload } = useAddToCart()

const {
  getProductsRecentlyViewed: getPerBrandProductsRecentlyViewed,
  addProductToRecentlyViewedCookie: addPerBrandProductToRecentlyViewed,
} = useRecentlyViewed(`${props.brand}-recently-viewed`)

const { data: recentlyViewedProducts } =
  await getPerBrandProductsRecentlyViewed(30, 0, [props.productId], {
    lazy: true,
    server: false,
  })

const { isShoppable } = useIsShoppable()

const nuxtApp = useNuxtApp()
const hideForCustomers = ref(false)
onMounted(async () => {
  const { hideForCustomers: hideForCustomersValue } =
    await nuxtApp.runWithContext(() =>
      useHideForCustomerTags('user-tags-eligibility')
    )
  hideForCustomers.value = hideForCustomersValue
})

onMounted(() => {
  scrollIntoPageView({
    scrollBothStrips: true,
    dynamicScrollPlp: false,
  })

  const deferNonCriticalWork = () => {
    const scrollData = JSON.parse(localStorage.getItem('scrollYVal') ?? '{}')
    scrollData.prodPage = true
    scrollData.scroll = `productTile-${props.productId}`
    localStorage.setItem('scrollYVal', JSON.stringify(scrollData))
    addProductToRecentlyViewed(props.productId)
    addPerBrandProductToRecentlyViewed(props.productId)
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(deferNonCriticalWork)
  } else {
    setTimeout(deferNonCriticalWork, 1)
  }
})

const { breadcrumbs } = useBreadcrumbs({
  dataOrigin: 'algolia',
  currentPageTitle: props.title,
  productCategories: props.mainCategory,
})

const linkToMainCategory = computed(() => {
  const arrBc = breadcrumbs.value
  if (!arrBc?.length || !props.mainCategory?.length) return undefined
  const lastCategoryName = props.mainCategory.at(-1)?.name?.toLowerCase()
  if (!lastCategoryName) return undefined
  return arrBc.find(item => item?.label.toLowerCase() === lastCategoryName)?.url
})

const oneSize = computed(() => {
  return (
    props.sizes?.[props.selectedCountry]?.[0]?.['size'] === 'TU' ||
    props.isSingleSizeByMicroCat
  )
})

const { breakpoint } = useProjectBreakpoint()
const isLarge = breakpoint.greaterOrEqual('lg')
const isMobile = breakpoint.smaller('md')

// Sticky bar: shown from the complete the look section until the end of the
// pdp content, so it never covers the footer
const stickyBarTrigger = ref<HTMLElement>()
const stickyBarEnd = ref<HTMLElement>()
const hasReachedCompleteTheLook = ref(false)
const hasReachedFooter = ref(false)

const isScrolledPast = ({
  isIntersecting,
  boundingClientRect,
}: IntersectionObserverEntry) => isIntersecting || boundingClientRect.top < 0

useIntersectionObserver(
  stickyBarTrigger,
  ([entry]) => {
    hasReachedCompleteTheLook.value = isScrolledPast(entry)
  },
  { threshold: 0 }
)

useIntersectionObserver(
  stickyBarEnd,
  ([entry]) => {
    hasReachedFooter.value = isScrolledPast(entry)
  },
  { threshold: 0 }
)

const isStickyBarVisible = computed(
  () => hasReachedCompleteTheLook.value && !hasReachedFooter.value
)

const isSliderNeeded = (items: number) =>
  isLarge.value ? items > 5 : items > 2

// EIFE-3600: temporary XGen similar products, shown only with ?xgenSimilar=true.
const route = useRoute()
const isXgenSimilarProducts = computed(() => route.query.xgenSimilar === 'true')

// Similar products only (not the other related carousels). On desktop, once
// that block leaves the viewport while the page scrolls back up, the sticky
// info column is about to return: put it back at the top so product details
// are not left mid-scroll.
const similarProductsRef = ref<ComponentPublicInstance | null>(null)
let previousWindowScrollY = 0
let isScrollingTowardTop = false

const syncScrollDirection = () => {
  const scrollY = window.scrollY
  isScrollingTowardTop = scrollY < previousWindowScrollY
  previousWindowScrollY = scrollY
}

const resetInfoColumnScroll = () => {
  const infoColumn = document.querySelector<HTMLElement>(
    '[data-pdp-info-scroll]'
  )
  if (!infoColumn || infoColumn.scrollTop === 0) return
  infoColumn.scrollTo({ top: 0, behavior: 'smooth' })
}

useIntersectionObserver(
  similarProductsRef,
  ([entry]) => {
    if (!entry || !isLarge.value) return
    if (!entry.isIntersecting && isScrollingTowardTop) {
      resetInfoColumnScroll()
    }
  },
  { threshold: 0 }
)

onMounted(() => {
  previousWindowScrollY = window.scrollY
  window.addEventListener('scroll', syncScrollDirection, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', syncScrollDirection)
})

// Sticky panes (gallery, right column) hand their scroll to the page: past the
// strips on the way in, and again once a pane reaches its edge mid-gesture.
usePdpPaneScroll()

const handleOpenSidebar = (sidebarId: string) => openDialog(sidebarId)

const selectedProductTag = ref('')
const handleTagClick = (tag: string) => {
  selectedProductTag.value = tag
  openDialog(SIDEBAR_IDS.pdpEcommerceBlockProductTagSideSlide)
}

const handleSimilarProductsEvent = () => {
  const id = 'similarProducts'
  scrollToAnchor(document.getElementById(id) ? id : 'youMayAlsoLike')
}

const scrollToAnchor = (id: string) => {
  const element = document.getElementById(id)
  if (!element) return
  const elementOffsetTop = element.getBoundingClientRect().top
  if (!isMobile.value) {
    const headerMonogram = document.getElementById('header-monogram')
    if (headerMonogram) {
      let headerHeight = headerMonogram.clientHeight
      const stripsContainer = document.getElementById('strips-container')
      if (stripsContainer) {
        headerHeight = stripsContainer.clientHeight
      }
      const scrollY = window.scrollY
      if (scrollY < headerHeight) {
        window.scrollTo({
          top: elementOffsetTop - (headerHeight - 20),
          behavior: 'instant',
        })
      } else if (scrollY === headerHeight) {
        window.scrollTo({ top: elementOffsetTop + 20, behavior: 'instant' })
      } else {
        element.scrollIntoView({ behavior: 'instant' })
      }
    }
  } else {
    window.scrollBy({ top: elementOffsetTop - 30, behavior: 'instant' })
  }
}

const productDetailsProps = computed(
  (): PdpEcommerceBlockProductDetailsRedesignProps => ({
    ...props,
    breadcrumbs: breadcrumbs.value ?? [], // ?? prop not being used inside PdpEcommerceBlockProductDetails
    isShoppable: isShoppable.value,
  })
)

const swiperRef = ref(null)
const { paneScrollStyle: galleryScrollStyle } = usePdpPaneOwnScroll(swiperRef)
const completeTheLookSection = ref(null)

// enable gallery own scroll only when the gallery is more than 70% of the viewport
const GALLERY_OWN_SCROLL_MIN_RATIO = 0.9
const hasGalleryOwnScroll = ref(true)

useIntersectionObserver(
  swiperRef,
  ([{ intersectionRatio }]) => {
    hasGalleryOwnScroll.value =
      intersectionRatio >= GALLERY_OWN_SCROLL_MIN_RATIO
  },
  { threshold: [0, GALLERY_OWN_SCROLL_MIN_RATIO, 1] }
)

/**
 * Quick Buy Popup
 */
const { openQuickBuySlide } = useUIQuickBuyPopUpSlide()

const openQuickBuyPopUpLayer = (product: ProductCardHorizontal) => {
  openQuickBuySlide({
    product,
    isQuickBuyPlp: true,
  })
}

/** Set to false to restore the carousel for "You might also like". */
const USE_ALSO_LIKE_GRID = true

const ALSO_LIKE_INITIAL_CL_FETCH = 10

const breadcrumbLabel = computed(() => {
  if (!breadcrumbs.value || breadcrumbs.value.length - 2 < 0) return ''
  return breadcrumbs.value.at(-2)?.label
})

/* Pdp 3D image */
const { openDialog: open3dImageDialog } = useDialog(SIDEBAR_IDS.pdp3dImage)
const { openDialog: openVirtualMirrorDialog } = useDialog(
  SIDEBAR_IDS.pdpVirtualMirrorModal
)
const { openDialog: openCompleteTheLookDialog } = useDialog(
  SIDEBAR_IDS.pdpCompleteTheLookSideSlide
)
const {
  dispatchActionVirtualMirrorEvent,
  dispatchActionProductsYouMayAlsoLikeShowMoreEvent,
  dispatchActionVariantSelectionPdpEvent,
  dispatchActionCompleteTheLookPdpEvent,
} = useGAActionEvents()
const { ts } = useI18n()

const virtualMirrorMobileLabel = computed(() =>
  ts('pdp.virtualMirror.mobileLabel')
)
const showVirtualMirrorMobileLabel = computed(
  () =>
    virtualMirrorMobileLabel.value &&
    !virtualMirrorMobileLabel.value.startsWith('pdp.')
)

const openVirtualMirror = (eventDetail: string) => {
  dispatchActionVirtualMirrorEvent(
    'open_vto',
    eventDetail,
    props.productId,
    props.title
  )
  openVirtualMirrorDialog()
}
const product3dImage = computed(() => {
  return props.orderedGallery.find(item => item.type === 'TResponsiveImage3D')
})

const getProductVirtualMirror = async (
  productToLoad: string
): Promise<VirtualMirrorProductData | null> => {
  if (!productToLoad?.trim()) return null
  const productData = await fetchAndMapProductData(productToLoad)
  if (!productData) return null

  const skuCode = productData.computedLists.skuList?.[0]
  const sizeLabel = productData.computedLists.sizeList?.[0]?.size ?? ''
  const firstUpc = skuCode
    ? (Object.values(productData.upcs ?? {})[0] as string | undefined)
    : null

  addToCartPayload.value = {
    name: productData.title,
    sku_code: skuCode ?? '',
    quantity: 1,
    metadata: {
      size: `${sizeLabel} (${props.selectedCountry})`,
      color: productData.selectedColor,
      queryID: productToLoad,
    },
  }

  return {
    upc: firstUpc ?? null,
    title: productData.title,
    price: productData.price,
    selectedColor: productData.selectedColor,
  }
}

const { ...virtualMirror } = useVirtualMirror()
const { showHeaderSearch } = useShowHeaderSearch()

const handleCompleteTheLookClick = () => {
  openCompleteTheLookDialog()
  dispatchActionCompleteTheLookPdpEvent({
    sku: props.productId,
    name: props.title,
    brand: props.brand,
  })
}

const handleSelectColor = (option: ColorOption) => {
  dispatchActionVariantSelectionPdpEvent({
    sku: props.productId,
    name: props.title,
    brand: props.brand,
    color: option.color,
  })
}

const hoveredColor = ref<ColorOption | null>(null)

const colorPreview = computed(
  () => hoveredColor.value?.thumbnailUrl || undefined
)
</script>

<template>
  <div>
    <PdpEcommerceBlockContainerRedesign
      id="pdp-main-block"
      class="carousel-container-overflowed bg-primitives-off-white lg:pb-[120px]"
      :class="{ giorgioArmaniHeaderPadding: offsetTop }"
    >
      <template #top-bar>
        <UtilsGoBack class="[&_span]:text-medium-7" :to="linkToMainCategory">
          <template #default>
            {{ breadcrumbLabel }}
          </template>
        </UtilsGoBack>
        <div class="flex items-center gap-2">
          <!-- Virtual Mirror button (sunglasses only) -->
          <button
            v-if="hasVirtualMirror"
            class="text-book-8 border-primitives-grey-100 bg-neutral-white flex h-10 items-center gap-2 border p-2 uppercase tracking-wider"
            @click="openVirtualMirror('product_image')"
          >
            <DefaultIconsVirtualMirror
              class="h-6 w-6"
              :aria-label="$ts('aria-labels.virtualMirror')"
            />
            <span v-if="showVirtualMirrorMobileLabel">{{
              virtualMirrorMobileLabel
            }}</span>
          </button>
          <button
            v-if="product3dImage"
            class="border-primitives-grey-100 bg-neutral-white h-10 border p-2"
            :aria-label="$ts('aria-labels.3dImage')"
            @click="open3dImageDialog()"
          >
            <DefaultIcons3DNew class="h-6 w-6" :aria-hidden="true" />
          </button>
        </div>
      </template>
      <template #right-side-pdp>
        <div class="lg:flex lg:flex-col">
          <div class="pb-md space-y-10">
            <PdpEcommerceBlockProductDetailsRedesign
              is-heading
              class="lg:!mt-0"
              :hide-for-customers="hideForCustomers"
              v-bind="productDetailsProps"
              :main-category="mainCategory"
              :selected-ext-sku="selectedExtSku"
              :size-grid-code="sizeGridCode"
              :is-fallback="isFallback"
              :this-product-dims="thisProductDims"
              :is-sticky-bar-visible="isStickyBarVisible"
              @update:selected-size-index="
                emit('update:selectedSizeIndex', $event)
              "
              @update:selected-country="emit('update:selectedCountry', $event)"
              @update:selected-ext-sku="emit('update:selectedExtSku', $event)"
              @open-sidebar="handleOpenSidebar"
              @tag-click="handleTagClick"
              @similar-products="handleSimilarProductsEvent()"
              @add-to-cart="emit('addToCart')"
              @update-wishlist="emit('update-wishlist')"
              @hover-color="hoveredColor = $event"
              @select-color="handleSelectColor"
            />
            <PdpEcommerceBlockProductInfoRedesign
              :product-details="productDetails"
              :product-care="productCare"
              :editorial-product-focus="editorialProductFocus"
              :product-focus="productFocus"
              :has-virtual-mirror="hasVirtualMirror"
              :fitting="fitting"
              :is-denim="isDenim"
              @open-virtual-mirror="openVirtualMirror($event)"
            />
          </div>
        </div>
      </template>
      <template #left-side-pdp>
        <div
          ref="swiperRef"
          data-pdp-gallery-scroll
          class="bg-primitives-off-white lg:scrollbar-hide col-span-12 shadow lg:sticky lg:top-[var(--header-default-height)] lg:order-1 lg:col-start-1 lg:col-end-9 lg:h-[calc(100dvh-var(--header-default-height))] lg:snap-y lg:snap-proximity lg:overflow-y-auto lg:shadow-none"
          :style="galleryScrollStyle"
        >
          <div class="relative">
            <!-- Desktop thumbnails -->
            <PdpEcommerceBlockGalleryThumbnailsRedesign
              :gallery="orderedGallery"
            />
            <!-- Main product gallery -->
            <PdpEcommerceBlockGallerySliderRedesign
              :gallery="orderedGallery"
              :color-preview="colorPreview"
              :title="title"
              :price="price"
              :is-complete-the-look-available="!!completeTheLookSection"
              :has-virtual-mirror="hasVirtualMirror"
              @complete-the-look="handleCompleteTheLookClick"
              @open-virtual-mirror="openVirtualMirror('sticky_cta')"
            >
              <template #complete-the-look-cta="{ isCollapsed }">
                <VisenzeRecommendations type="STL" :product-sku="productId">
                  <template #default="{ products }">
                    <PdpEcommerceBlockCompleteTheLookCta
                      v-if="products?.length"
                      :label="
                        $ts(
                          'pdp.productDetails.informationSection.completeTheLookCta'
                        )
                      "
                      :items="
                        products.slice(0, 3).map(product => ({
                          id: product.product_id,
                          imageUrl: product.main_image_url,
                          alt: product.data?.title ?? '',
                        }))
                      "
                      :is-collapsed="isCollapsed"
                      @click="handleCompleteTheLookClick"
                    />
                  </template>
                </VisenzeRecommendations>
              </template>
            </PdpEcommerceBlockGallerySliderRedesign>
          </div>
          <div
            class="bg-primitives-off-white sticky bottom-0 z-50 mt-5 hidden p-2 lg:block"
          >
            <UIBreadcrumbs
              class="col-span-8 max-w-full"
              :items="breadcrumbs ?? []"
              pdp
              has-fade-out
            />
          </div>
          <PdpEcommerceBlockProductNameRedesign
            :product-code="productId"
            class="mt-[16px] lg:hidden"
            :title="title"
            :price="price"
            :old-price="oldPrice"
            :discount="discount"
            :tags="tags"
            :is-shoppable="isShoppable && !infoCommerce"
            @update-wishlist="emit('update-wishlist')"
            @tag-click="handleTagClick"
          />
        </div>
      </template>
    </PdpEcommerceBlockContainerRedesign>

    <DelayHydration>
      <XGenRecommendations
        v-if="isShoppable && isXgenSimilarProducts"
        :prediction="PDP_SIMILAR_PRODUCTS_ID"
        :initial-cl-fetch="10"
        tracking-name="similar_products"
      >
        <template #default="{ products }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="similarProducts"
            ref="similarProductsRef"
            :title="$ts('pdp.relatedProducts.similarProducts')"
          >
            <UISliderWithArrows
              :id="`XSE-${PDP_SIMILAR_PRODUCTS_ID}`"
              :items="products"
              :loop="isSliderNeeded(products.length)"
              :show-navigation="isSliderNeeded(products.length)"
              class="XGen_SmartElement"
              has-slide-vertical-border
              :style="isSliderNeeded(products.length) ? '' : '--padding-x: 1px'"
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      :disable-swipe-mobile="true"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PDP_SIMILAR_PRODUCTS_ID}`,
                      }"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </XGenRecommendations>
      <VisenzeRecommendations
        v-else-if="isShoppable"
        type="VSR"
        :product-sku="productId"
      >
        <template #default="{ products }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="similarProducts"
            ref="similarProductsRef"
            :title="$ts('pdp.relatedProducts.similarProducts')"
          >
            <UISliderWithArrows
              :items="products"
              :loop="isSliderNeeded(products.length)"
              :show-navigation="isSliderNeeded(products.length)"
              has-slide-vertical-border
              :style="isSliderNeeded(products.length) ? '' : '--padding-x: 1px'"
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      :disable-swipe-mobile="true"
                      :product="product"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </VisenzeRecommendations>
    </DelayHydration>

    <!-- Marks where the sticky add to cart bar starts showing up -->
    <div ref="stickyBarTrigger" aria-hidden="true" />

    <DelayHydration>
      <VisenzeRecommendations type="STL" :product-sku="productId">
        <template #default="{ products, bestImage }">
          <LazyPdpCompleteLookRedesign
            v-if="products && products.length && showHeaderSearch"
            id="completeTheLook"
            ref="completeTheLookSection"
            :title="$ts('pdp.relatedProducts.completeTheLook')"
          >
            <template #product>
              <NuxtPicture
                provider="cloudinary"
                :src="bestImage ?? (orderedGallery[0].data as string)"
                :placeholder="[56, 70]"
                :alt="title"
                sizes="sm:50vw lg:560px"
                class="aspect-[560/702] w-full"
                :img-attrs="{
                  class: 'h-full w-full object-contain',
                }"
                :modifiers="{ width: 560, height: 702 }"
                quality="70"
                loading="lazy"
                fit="fit"
              />
            </template>
            <template #slider>
              <template v-if="products?.length">
                <!-- Mobile, and desktop too when the grid would exceed 4 cards -->
                <div
                  class="related-product-carousel"
                  :class="{ 'min-[1100px]:hidden': products.length <= 4 }"
                >
                  <UISliderWithArrows
                    :items="products"
                    :loop="false"
                    :slide-custom-width="
                      products.length > 4
                        ? 'w-[calc(100%_/_1.5)] min-[1100px]:w-1/2'
                        : 'w-[calc(100%_/_1.5)]'
                    "
                    :show-scrollbar="false"
                    :show-navigation-prev-arrow="products.length > 4"
                    :has-slide-vertical-border="true"
                    :hide-lateral-slides-on-mobile="isLarge ? false : true"
                  >
                    <template #default="item">
                      <WrapperProductTile :product="item">
                        <template #default="{ product, productCardHorizontal }">
                          <UIProductTile
                            :disable-swipe-mobile="true"
                            :product="product"
                            :only-img="!isLarge"
                            @open-quick-buy="
                              () =>
                                productCardHorizontal &&
                                openQuickBuyPopUpLayer(productCardHorizontal)
                            "
                          />
                        </template>
                      </WrapperProductTile>
                    </template>
                  </UISliderWithArrows>
                </div>

                <!-- Desktop: full border if <2 cards; shared edges if 2+ -->
                <div
                  v-if="products.length <= 4"
                  class="ctl-desktop-grid hidden grid-cols-2 min-[1100px]:grid"
                  :class="{
                    'border-primitives-black border-l border-t':
                      products.length >= 2,
                  }"
                >
                  <WrapperProductTile
                    v-for="(item, index) in products"
                    :key="item?.product_id ?? item?.sku ?? index"
                    :product="item"
                  >
                    <template #default="{ product, productCardHorizontal }">
                      <div
                        class="ctl-desktop-card border-primitives-black bg-primitives-off-white"
                        :class="
                          products.length < 2 ? 'border' : 'border-b border-r'
                        "
                      >
                        <UIProductTile
                          :disable-swipe-mobile="true"
                          :product="product"
                          @open-quick-buy="
                            () =>
                              productCardHorizontal &&
                              openQuickBuyPopUpLayer(productCardHorizontal)
                          "
                        />
                      </div>
                    </template>
                  </WrapperProductTile>
                </div>
              </template>
            </template>
          </LazyPdpCompleteLookRedesign>
        </template>
      </VisenzeRecommendations>
    </DelayHydration>

    <DelayHydration>
      <CarouselTrackingProvider name="recently_viewed" partner="">
        <LazyPdpRelatedProducts
          v-if="recentlyViewedProducts?.length"
          id="recentlyViewed"
          :title="$ts('myAccount.logged.recentlyViewed.title')"
          class="!px-0"
        >
          <UISliderWithArrows
            :items="recentlyViewedProducts"
            :loop="false"
            has-slide-vertical-border
            slide-custom-width="w-1/2 lg:w-1/5"
          >
            <template #default="item">
              <WrapperProductTile :product="item">
                <template #default="{ product, productCardHorizontal }">
                  <UIProductTile
                    :disable-swipe-mobile="true"
                    :product="product"
                    @open-quick-buy="
                      () =>
                        productCardHorizontal &&
                        openQuickBuyPopUpLayer(productCardHorizontal)
                    "
                  />
                </template>
              </WrapperProductTile>
            </template>
          </UISliderWithArrows>
        </LazyPdpRelatedProducts>
      </CarouselTrackingProvider>
    </DelayHydration>

    <DelayHydration>
      <XGenRecommendations
        :prediction="PDP_YOU_MAY_ALSO_LIKE_ID_REDESIGN"
        :initial-cl-fetch="ALSO_LIKE_INITIAL_CL_FETCH"
        tracking-name="products_you_may_like"
      >
        <template #default="{ products, loadMore }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="youMayAlsoLike"
            :title="$ts('pdp.relatedProducts.youMayAlsoLike')"
            :variant="USE_ALSO_LIKE_GRID ? 'grid' : 'carousel'"
          >
            <PdpAlsoLikeGrid
              v-if="USE_ALSO_LIKE_GRID"
              :products="products"
              :prediction-id="PDP_YOU_MAY_ALSO_LIKE_ID_REDESIGN"
              :hide-for-customers="hideForCustomers"
              :initial-tracked-count="ALSO_LIKE_INITIAL_CL_FETCH"
              @open-quick-buy="openQuickBuyPopUpLayer"
              @load-more="loadMore"
              @emit-ga-load-more-event="
                dispatchActionProductsYouMayAlsoLikeShowMoreEvent({
                  sku: productId,
                  name: title,
                  brand,
                })
              "
            />
            <UISliderWithArrows
              v-else
              :id="`XSE-${PDP_YOU_MAY_ALSO_LIKE_ID_REDESIGN}`"
              :items="products"
              class="XGen_SmartElement"
              has-slide-vertical-border
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      :disable-swipe-mobile="true"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PDP_YOU_MAY_ALSO_LIKE_ID_REDESIGN}`,
                      }"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </XGenRecommendations>
    </DelayHydration>

    <DelayHydration>
      <div
        v-show="editorialDeliveryKeys && editorialDeliveryKeys.length"
        class="editorial-modules-wrapper"
      >
        <EditorialModulesWrapper
          v-for="deliveryKey of editorialDeliveryKeys"
          :key="deliveryKey"
          :delivery-key="deliveryKey"
        />
      </div>
    </DelayHydration>

    <div
      class="lg:pt-15 flex flex-col items-center gap-4 px-5 py-12"
      :class="
        isEa7Website || isEmporioArmaniWebsite
          ? 'bg-neutral-black text-neutral-white'
          : 'bg-primitives-off-white text-neutral-black'
      "
    >
      <p class="text-book-5">
        {{ $ts('pdp.searchBar.title') }}
      </p>
      <button
        class="text-book-6 flex h-[50px] w-full max-w-[400px] items-center gap-2 rounded-full border px-4 py-2"
        :class="
          isEa7Website || isEmporioArmaniWebsite
            ? 'border-neutral-white'
            : 'border-neutral-black'
        "
        :aria-label="ts('search.label')"
        aria-haspopup="dialog"
        @click="openSearchView('pdp')"
      >
        <DefaultIconsSearch aria-hidden="true" />
        <span class="text-book-6 underline-effect-appear relative text-nowrap">
          <span class="text-effect outline-none">
            {{ ts('search.label') }}
          </span>
        </span>
      </button>
    </div>

    <div class="pdp-anchors-list-container">
      <EditorialModulesWrapper
        :delivery-key="`${currentWebsite}-anchors-strip-pdp`"
      />
    </div>

    <!-- Marks where the sticky add to cart bar hides, before the footer -->
    <div ref="stickyBarEnd" aria-hidden="true" />

    <!-- SIDEBARS -->

    <WrapperReserveInStore
      :id-product="productId"
      :image="orderedGallery[0].data as string"
      :product="productDetailsProps"
      :selected-size-index="oneSize ? 0 : selectedSizeIndex"
      :is-single-size-by-micro-cat="props.isSingleSizeByMicroCat"
      :selected-country="selectedCountry"
      :sizes="sizes"
      :upcs="upcs"
      @update:selected-country="emit('update:selectedCountry', $event)"
    />
    <PdpInfoShippingItem />
    <PdpVirtualMirrorModal
      v-if="hasVirtualMirror"
      :get-product-virtual-mirror="getProductVirtualMirror"
      v-bind="virtualMirror"
      :product-details="productDetailsProps"
    />
    <PdpMessageSideSlide
      v-if="productDetailsProps.pdpMessage"
      v-bind="productDetailsProps.pdpMessage"
    />
    <PdpCompleteTheLookSideSlide
      :product-id="productId"
      :title="title"
      :product-image="orderedGallery[0].data as string"
    />
    <!-- TODO: CHECK CONDITION FOR FINAL SALES SIDEBAR -->
    <PdpInfoReturnItem :sidebar-id="SIDEBAR_IDS.pdpInfoReturnItem" />
    <PdpProductTagSideSlide
      :tag="selectedProductTag"
      :brand="brand"
      :gender="productGender"
      :exclude-object-id="productId"
    />
    <PdpInfoFriendsFamily
      v-if="isFriendsFamily && hideForCustomers"
      :sidebar-id="SIDEBAR_IDS.pdpFriendsFamily"
    />
    <PdpImage3dModal
      v-if="product3dImage"
      :model="product3dImage?.data?.model ?? ''"
    />
  </div>
</template>

<style lang="scss" scoped>
.swiper :deep(.swiper-pagination) {
  text-align: left;
}

.swiper :deep(.swiper-wrapper) {
  @screen lg {
    transform: none !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;
  }
}

.swiper :deep(.swiper-slide) {
  background-color: transparent !important;
  padding-bottom: 3rem;
}

@media (min-width: 1024px) {
  .swiper :deep(.swiper-slide) {
    padding: 0 !important;
    height: 100vh !important;
    width: 100% !important;
    display: flex !important;
    align-content: center !important;
  }
}

.shadow {
  -webkit-box-shadow: 0 0 20px 20px #fbfbfb;
  box-shadow: 0 0 10px 10px #fbfbfb;
}

/* UIProductTile uses a grey outline that sits on top of the wrapper border */
.ctl-desktop-card :deep(.outline-stroke-cards-hover),
.ctl-desktop-card :deep(.outline-stroke-cards-hover:hover) {
  outline: none;
}

.giorgioArmaniHeaderPadding {
  :deep(.giorgioArmaniHeaderPaddingTop) {
    padding-top: var(--header-default-height);
  }
}
</style>
